import { FC } from "react";

import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import { SxProps, Theme } from "@mui/material/styles";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";

type CaptureKeyframeButtonProps = {
  tooltipTitle: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  placement?: TooltipProps["placement"];
  containerSx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  buttonProps?: Omit<
    ButtonProps,
    "onClick" | "children" | "disabled" | "startIcon"
  >;
};

const mergeSx = (
  base: SxProps<Theme>,
  additional?: SxProps<Theme>
): SxProps<Theme> => {
  if (!additional) {
    return base;
  }

  if (Array.isArray(additional)) {
    return [base, ...additional] as SxProps<Theme>;
  }

  return [base, additional] as SxProps<Theme>;
};

const baseContainerSx: SxProps<Theme> = {
  marginRight: "0.5em",
};

const baseButtonSx: SxProps<Theme> = {
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    color: (theme: Theme) => theme.palette.text.disabled,
  },
  ":hover": {
    backgroundColor: "transparent",
  },
};

const CaptureKeyframeButton: FC<CaptureKeyframeButtonProps> = ({
  tooltipTitle,
  label,
  onClick,
  disabled = false,
  placement = "top",
  containerSx,
  buttonSx,
  buttonProps,
}) => {
  return (
    <Tooltip title={tooltipTitle} placement={placement}>
      <Box sx={mergeSx(baseContainerSx, containerSx)}>
        <Button
          disabled={disabled}
          variant="text"
          size="small"
          color="inherit"
          startIcon={<PhotoCameraOutlinedIcon />}
          onClick={onClick}
          sx={mergeSx(baseButtonSx, buttonSx)}
          {...buttonProps}
        >
          {label}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default CaptureKeyframeButton;
